/**
 * src/pages/SuiviPage.tsx — Onglet "Suivi" : arborescence Clients → Sites → Compteurs (PDL/PCE).
 *
 * Tant qu'aucun nœud n'est sélectionné, l'arbre occupe toute la largeur. Dès qu'un nœud est
 * sélectionné, l'arbre se range dans une colonne étroite à gauche et l'espace libéré affiche le
 * détail du nœud à droite (client → liste de ses sites ; site → onglets Info/Synthèse ; compteur →
 * fiche + bouton "Voir les consommations" qui réutilise MeterConsoModal, déjà responsable de
 * l'affichage des courbes de charge/relevés dans l'onglet Paramètres → PDL/PCE).
 */

import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { useAuthContext } from "../auth/AuthContext";
import { getPermissions } from "../auth/roles";
import { listClients, listSites, groupSitesByClient, type ClientRecord, type SiteRecord } from "../api/settingsApi";
import { listPdl, listPce, type PdlRecord, type PceRecord } from "../api/metersApi";
import { getMonthlyElec, getDailyElec, getAvgDay, type TaggedMeter, type MeterSource } from "../api/energyApi";
import { getMonthlyGaz, getConsoGazDetail, type TaggedGazMeter, type GazSource, type MonthlyGazResponse } from "../api/gazApi";
import { loadHCAndRender } from "../components/blocks/shared/energyChart";
import { FF_ENERGY } from "../components/blocks/shared/constants";
import { PROP_LABELS } from "./settings/helpers";
import { MeterConsoModal } from "./settings/MeterConsoModal";

type Selected =
  | { kind: "client"; id: string }
  | { kind: "site"; id: string }
  | { kind: "meter"; id: string; isPdl: boolean };

type SiteDetailTab = "info" | "synthese";
type SynthesePreset = "current" | "year" | "quarter" | "month";

const SOURCE_STYLE: Record<string, { label: string; color: string }> = {
  enedis: { label: "Enedis", color: "#2196f3" },
  grdf:   { label: "GRDF",   color: "#2196f3" },
  deepki: { label: "Deepki", color: "var(--green-dim)" },
  engie:  { label: "Engie",  color: "#8e44ad" },
};

const PRESET_LABELS: Record<SynthesePreset, string> = {
  current: "Année en cours", year: "Année", quarter: "Trimestre", month: "Mois",
};

const MONTH_LABELS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const MONTH_SHORT  = ["Janv.","Févr.","Mars","Avril","Mai","Juin","Juil.","Août","Sept.","Oct.","Nov.","Déc."];

const DETAILS_KEYS = ["anneeConstruction","anneeRehabiliation","occupant","configuration","typologie","surface","horaire"];
const CVC_KEYS      = ["chauffage","rafraichissement"];

/** Mois "YYYY-MM" couverts par un preset de période — partagé par l'onglet Synthèse d'un site et
 * le graphique des relevés d'un compteur gaz (mêmes inputs, cf. PeriodPresetSelector). */
function monthsForPreset(preset: SynthesePreset, year: number, quarter: number, month: number, nowYear: number): string[] {
  if (preset === "current") return Array.from({length:12}, (_,i) => `${nowYear}-${String(i+1).padStart(2,"0")}`);
  if (preset === "year")    return Array.from({length:12}, (_,i) => `${year}-${String(i+1).padStart(2,"0")}`);
  if (preset === "quarter") { const s = (quarter-1)*3+1; return [s,s+1,s+2].map(m => `${year}-${String(m).padStart(2,"0")}`); }
  return [`${year}-${String(month).padStart(2,"0")}`];
}

/** Équivalent `monthsForPreset`, mais sous la forme d'un unique `period` "YYYY"/"YYYY-TN"/"YYYY-MM"
 * attendue par getDailyElec/getAvgDay (cf. matchesPeriod côté ts-ingest — même format que
 * buildPeriodKey dans report-engine/datasets.ts, "T" et pas "Q" pour un trimestre). */
function periodKeyForPreset(preset: SynthesePreset, year: number, quarter: number, month: number, nowYear: number): string {
  if (preset === "current") return `${nowYear}`;
  if (preset === "year")    return `${year}`;
  if (preset === "quarter") return `${year}-T${quarter}`;
  return `${year}-${String(month).padStart(2,"0")}`;
}

/** Sélecteur année/trimestre/mois (masqué pour le preset "current", rien à choisir) — extrait de
 * `PeriodPresetSelector` pour pouvoir le placer ailleurs que juste sous la ligne de presets (ex. à
 * côté de la box total, cf. `hideDetailRow` ci-dessous, ElecMeterConso/GazMeterReleves). */
function PeriodDetailInputs({ preset, year, quarter, month, onChangeYear, onChangeQuarter, onChangeMonth }: {
  preset: SynthesePreset; year: number; quarter: number; month: number;
  onChangeYear: (y: number) => void;
  onChangeQuarter: (q: number) => void;
  onChangeMonth: (m: number) => void;
}) {
  if (preset === "current") return null;
  const nowYear = new Date().getFullYear();
  const yearsRange = Array.from({length:6}, (_,i) => nowYear - i);
  return (
    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
      <select value={year} onChange={e => onChangeYear(Number(e.target.value))} className="field" style={{width:90,marginBottom:0,fontSize:12}}>
        {yearsRange.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      {preset === "quarter" && (
        <select value={quarter} onChange={e => onChangeQuarter(Number(e.target.value))} className="field" style={{width:70,marginBottom:0,fontSize:12}}>
          {[1,2,3,4].map(q => <option key={q} value={q}>T{q}</option>)}
        </select>
      )}
      {preset === "month" && (
        <select value={month} onChange={e => onChangeMonth(Number(e.target.value))} className="field" style={{width:120,marginBottom:0,fontSize:12}}>
          {MONTH_LABELS.map((lbl,i) => <option key={i} value={i+1}>{lbl}</option>)}
        </select>
      )}
    </div>
  );
}

/** Sélecteur de période partagé (presets + année/trimestre/mois) — mêmes inputs pour l'onglet
 * Synthèse d'un site (SiteSyntheseTab) et le graphique des relevés d'un compteur gaz (GazMeterReleves).
 * `trailing` : contenu optionnel justifié à droite sur la même ligne que les presets (ex. le
 * sélecteur de granularité heure/jour/mois d'ElecMeterConso). `hideDetailRow` : n'affiche pas la
 * ligne année/trimestre/mois ici — le consommateur la place lui-même via `PeriodDetailInputs`
 * (ex. sur la même ligne que la box total). */
function PeriodPresetSelector({ preset, year, quarter, month, onChangePreset, onChangeYear, onChangeQuarter, onChangeMonth, trailing, hideDetailRow }: {
  preset: SynthesePreset; year: number; quarter: number; month: number;
  onChangePreset: (p: SynthesePreset) => void;
  onChangeYear: (y: number) => void;
  onChangeQuarter: (q: number) => void;
  onChangeMonth: (m: number) => void;
  trailing?: ReactNode;
  hideDetailRow?: boolean;
}) {
  return (
    <>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
        {(Object.keys(PRESET_LABELS) as SynthesePreset[]).map(p => (
          <button key={p} onClick={() => onChangePreset(p)}
            style={{fontSize:11,padding:"5px 12px",borderRadius:20,
              border: preset===p ? "1.5px solid var(--green-dim)" : "1.5px solid #ddd",
              background: preset===p ? "var(--green-dim)" : "transparent",
              color: preset===p ? "#fff" : "var(--text-muted)",
              cursor:"pointer", fontFamily:"var(--ff-title)", fontWeight:preset===p?700:500}}>
            {PRESET_LABELS[p]}
          </button>
        ))}
        {trailing && <div style={{marginLeft:"auto",display:"flex",gap:6,flexWrap:"wrap"}}>{trailing}</div>}
      </div>

      {!hideDetailRow && preset !== "current" && (
        <div style={{marginBottom:18}}>
          <PeriodDetailInputs preset={preset} year={year} quarter={quarter} month={month}
            onChangeYear={onChangeYear} onChangeQuarter={onChangeQuarter} onChangeMonth={onChangeMonth}/>
        </div>
      )}
    </>
  );
}

function SourceBadge({ source }: { source: string }) {
  const s = SOURCE_STYLE[source];
  if (!s) return <span title="Source non configurée" style={{fontSize:10,fontWeight:700,color:"var(--text-faint)",border:"1px solid #ddd",borderRadius:10,padding:"1px 7px",lineHeight:1.4}}>Non configurée</span>;
  return <span title={`Source : ${s.label}`} style={{fontSize:10,fontWeight:700,color:s.color,border:`1px solid ${s.color}`,borderRadius:10,padding:"1px 7px",lineHeight:1.4}}>{s.label}</span>;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <span style={{display:"inline-block",width:14,flexShrink:0,color:"var(--text-faint)",
      transform:open?"rotate(90deg)":"none",transition:"transform 0.12s",fontSize:11}}>
      ▸
    </span>
  );
}

/** Miniature cliquable — cf. ImageLightbox pour l'affichage plein écran. */
function ImageThumb({ label, src, onOpen }: { label: string; src: string; onOpen: () => void }) {
  return (
    <button onClick={onOpen} title={`Voir ${label} en plein écran`}
      style={{width:"100%",aspectRatio:"4 / 3",border:"1px solid var(--border-light)",borderRadius:8,
        overflow:"hidden",cursor:"pointer",padding:0,background:"#222",position:"relative"}}>
      <img src={src} alt={label} style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
      <span style={{position:"absolute",bottom:0,left:0,right:0,padding:"5px 8px",
        background:"linear-gradient(transparent,rgba(0,0,0,0.65))",
        fontSize:10,fontWeight:700,color:"#fff",fontFamily:"var(--ff-title)",
        textTransform:"uppercase",letterSpacing:0.5}}>
        {label}
      </span>
    </button>
  );
}

function EmptyThumb({ label, icon }: { label: string; icon: string }) {
  return (
    <div style={{width:"100%",aspectRatio:"4 / 3",border:"1px dashed var(--border-light)",borderRadius:8,
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,color:"var(--text-faint)"}}>
      <span style={{fontSize:22}}>{icon}</span>
      <span style={{fontSize:10,fontFamily:"var(--ff-title)",textTransform:"uppercase",letterSpacing:0.5}}>{label}</span>
    </div>
  );
}

function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{position:"fixed",inset:0,zIndex:4000,background:"rgba(0,0,0,0.85)",
        display:"flex",alignItems:"center",justifyContent:"center",padding:32}}>
      <button onClick={onClose} title="Fermer"
        style={{position:"absolute",top:20,right:24,background:"none",border:"none",
          cursor:"pointer",fontSize:28,color:"#fff",lineHeight:1}}>
        ✕
      </button>
      <img src={src} alt={alt} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",borderRadius:4}}/>
    </div>
  );
}

/** Camembert Élec/Gaz — mêmes conventions de chargement/rendu Highcharts que les blocs de rapport
 * (cf. CLAUDE.md, "Highcharts" : doRenderRef.current + useEffect, nettoyage des instances orphelines). */
function EnergyPieChart({ elecKwh, gazKwh }: { elecKwh: number; gazKwh: number }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const instRef  = useRef<any>(null);
  const doRender = useRef<() => void>(() => {});

  doRender.current = () => {
    if (!chartRef.current || !window.Highcharts) return;
    if (instRef.current) { try { instRef.current.destroy(); } catch {} instRef.current = null; }
    ((window.Highcharts.charts ?? []) as any[]).forEach(ch => {
      if (ch && ch.renderTo === chartRef.current) { try { ch.destroy(); } catch {} }
    });
    instRef.current = window.Highcharts.chart(chartRef.current, {
      chart:{ type:"pie", backgroundColor:"transparent", height:280, style:{ fontFamily: FF_ENERGY } },
      title:{ text: undefined }, credits:{ enabled:false },
      tooltip:{ pointFormat:"<b>{point.y:,.0f} kWh</b> ({point.percentage:.1f} %)" },
      plotOptions:{ pie:{ borderWidth:0, dataLabels:{ enabled:true, format:"{point.name}<br/>{point.percentage:.1f} %",
        style:{ fontFamily:FF_ENERGY, fontSize:"11px", fontWeight:"600", textOutline:"none" } } } },
      series:[{ type:"pie", name:"Consommation", data:[
        { name:"Électricité", y: elecKwh, color:"#f5c518" },
        { name:"Gaz",         y: gazKwh,  color:"#e8703a" },
      ]}],
    });
  };

  useEffect(() => {
    let active = true;
    loadHCAndRender(() => { if (active) doRender.current(); });
    return () => { active = false; if (instRef.current) { try { instRef.current.destroy(); } catch {} instRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (window.Highcharts) doRender.current(); }, [elecKwh, gazKwh]);

  return <div ref={chartRef} style={{height:280,width:"100%"}}/>;
}

/** Onglet "Synthèse" d'un site — période choisie (presets) + camembert Élec/Gaz. Monté avec
 * `key={site.id}` par l'appelant : tout son état (preset/année/…) repart donc de zéro à chaque
 * changement de site sélectionné, sans reset manuel. */
function SiteSyntheseTab({ site, pdls, pces }: { site: SiteRecord; pdls: PdlRecord[]; pces: PceRecord[] }) {
  const nowYear = new Date().getFullYear();
  const [preset,  setPreset]  = useState<SynthesePreset>("current");
  const [year,    setYear]    = useState(nowYear);
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth()/3) + 1);
  const [month,   setMonth]   = useState(new Date().getMonth() + 1);
  const [data,    setData]    = useState<{ elec:number; gaz:number; hasData:boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string|null>(null);

  const months = useMemo(() => monthsForPreset(preset, year, quarter, month, nowYear), [preset, year, quarter, month, nowYear]);

  const allElecMeters = useMemo(() => pdls.filter(p => p.siteId === site.id), [pdls, site.id]);
  const allGazMeters  = useMemo(() => pces.filter(p => p.siteId === site.id), [pces, site.id]);
  const elecMeters = useMemo<TaggedMeter[]>(
    () => allElecMeters.filter(p => p.source).map(p => ({ id:p.id, source:p.source as MeterSource })),
    [allElecMeters]);
  const gazMeters = useMemo<TaggedGazMeter[]>(
    () => allGazMeters.filter(p => p.source).map(p => ({ id:p.id, source:p.source as GazSource })),
    [allGazMeters]);

  const hasAnyMeters        = allElecMeters.length > 0 || allGazMeters.length > 0;
  const hasConfiguredMeters = elecMeters.length > 0 || gazMeters.length > 0;

  useEffect(() => {
    if (!hasConfiguredMeters) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const years = Array.from(new Set(months.map(m => parseInt(m.slice(0,4), 10))));
        const [elecRes, gazRes] = await Promise.all([
          elecMeters.length ? getMonthlyElec(elecMeters, years) : Promise.resolve({ consoElecTotaleParAnneeMois:{} as Record<string,number> }),
          gazMeters.length  ? getMonthlyGaz(gazMeters, years)   : Promise.resolve({ consoGazTotaleParAnneeMois:{} } as MonthlyGazResponse),
        ]);
        if (cancelled) return;
        const elecValues = months.map(mk => elecRes.consoElecTotaleParAnneeMois[mk]);
        const gazValues  = months.map(mk => gazRes.consoGazTotaleParAnneeMois[mk]);
        const elecTotal = elecValues.reduce((s:number,v) => s + (v ?? 0), 0);
        const gazTotal  = gazValues.reduce((s:number,v) => s + (v ?? 0), 0);
        setData({ elec: elecTotal, gaz: gazTotal, hasData: elecValues.some(v => v != null) || gazValues.some(v => v != null) });
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [months, elecMeters, gazMeters, hasConfiguredMeters]);

  return (
    <div>
      <PeriodPresetSelector preset={preset} year={year} quarter={quarter} month={month}
        onChangePreset={setPreset} onChangeYear={setYear} onChangeQuarter={setQuarter} onChangeMonth={setMonth}/>

      {!hasAnyMeters ? (
        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Aucun compteur pour ce site.</div>
      ) : !hasConfiguredMeters ? (
        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Pas de source configurée.</div>
      ) : loading ? (
        <div style={{height:280,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:12}}>Chargement…</div>
      ) : error ? (
        <div style={{color:"var(--red)",fontSize:12}}>⚠ {error}</div>
      ) : !data || !data.hasData ? (
        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Aucune donnée pour cette période.</div>
      ) : (
        <>
          <EnergyPieChart elecKwh={data.elec} gazKwh={data.gaz}/>
          <div style={{display:"flex",gap:20,fontSize:12,color:"var(--text-muted)",marginTop:8,justifyContent:"center",flexWrap:"wrap"}}>
            <span>⚡ Électricité : <strong style={{color:"var(--text-primary)"}}>{Math.round(data.elec).toLocaleString("fr-FR")} kWh</strong></span>
            <span>🔥 Gaz : <strong style={{color:"var(--text-primary)"}}>{Math.round(data.gaz).toLocaleString("fr-FR")} kWh</strong></span>
          </div>
        </>
      )}
    </div>
  );
}

interface GazReleve { dateDebut: string; dateFin: string; energie: number; qualif: string; }

const dtFr = (iso: string) => { const [y,m,d] = iso.split("-"); return `${d}/${m}/${y}`; };
// Mois de début du relevé — via getUTC*, pas les getters locaux : `iso` ("YYYY-MM-DD") est parsé
// comme minuit UTC (`new Date("YYYY-MM-DD")`), donc les getters locaux décaleraient le mois pour
// tout fuseau à l'ouest de l'UTC (ex. un décalage négatif ferait retomber le 1er du mois sur le
// mois précédent).
const monthLabel = (iso: string) => { const d = new Date(iso); return `${MONTH_SHORT[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`; };

/** Un bâton par relevé, largeur proportionnelle au nombre de jours couverts (module Highcharts
 * "variwide", cf. loadHCAndRender(...,needVariwide=true)). Un relevé GRDF n'est pas mensuel : la
 * largeur variable rend cette irrégularité visible d'un coup d'œil, contrairement à un
 * histogramme à colonnes égales qui la masquerait. */
function GazReleveBarChart({ releves }: { releves: GazReleve[] }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const instRef  = useRef<any>(null);
  const doRender = useRef<() => void>(() => {});
  // Distinct de `window.Highcharts` (core) : le module "variwide" peut encore être en cours de
  // chargement même quand le core est déjà présent (chargé par un autre graphique de la page avant
  // le montage de celui-ci) — ne passe à true qu'une fois le module réellement chargé (callback de
  // loadHCAndRender), pour ne jamais appeler Highcharts.chart({type:"variwide"}) trop tôt (throw
  // synchrone "module requis" sinon).
  const [ready, setReady] = useState(false);

  doRender.current = () => {
    if (!chartRef.current || !window.Highcharts) return;
    if (instRef.current) { try { instRef.current.destroy(); } catch {} instRef.current = null; }
    ((window.Highcharts.charts ?? []) as any[]).forEach(ch => {
      if (ch && ch.renderTo === chartRef.current) { try { ch.destroy(); } catch {} }
    });

    const sorted = [...releves].sort((a,b) => a.dateDebut.localeCompare(b.dateDebut));
    const data = sorted.map(r => {
      const days = Math.max(1, Math.round((Date.parse(r.dateFin) - Date.parse(r.dateDebut)) / 86400000));
      return { name: monthLabel(r.dateDebut), rangeLabel: `${dtFr(r.dateDebut)} → ${dtFr(r.dateFin)}`,
        y: r.energie, z: days, color: r.qualif === "Mesuré" ? "#e8703a" : "#e8a838" };
    });

    instRef.current = window.Highcharts.chart(chartRef.current, {
      chart:{ type:"variwide", backgroundColor:"transparent", height:260, style:{ fontFamily: FF_ENERGY } },
      title:{ text: undefined }, legend:{ enabled:false }, credits:{ enabled:false },
      xAxis:{ type:"category", labels:{ enabled:true, style:{ fontSize:"10px", color:"var(--text-muted)" } },
        lineColor:"#ddd6c8", tickColor:"transparent" },
      yAxis:{ title:{ text:"kWh" }, gridLineColor:"#eee" },
      tooltip:{ headerFormat:"", pointFormat:"<b>{point.rangeLabel}</b><br/>{point.y:,.0f} kWh · {point.z} j" },
      series:[{ type:"variwide", name:"Relevés", data, color:"#e8703a" }],
    });
  };

  useEffect(() => {
    let active = true;
    loadHCAndRender(() => { if (active) { setReady(true); doRender.current(); } }, false, true);
    return () => { active = false; if (instRef.current) { try { instRef.current.destroy(); } catch {} instRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (ready) doRender.current(); }, [releves, ready]);

  return <div ref={chartRef} style={{height:260,width:"100%"}}/>;
}

/** Relevés d'un compteur gaz sur une période choisie — mêmes inputs que SiteSyntheseTab
 * (PeriodPresetSelector). Monté avec `key={meter.id}` par l'appelant. */
function GazMeterReleves({ meter }: { meter: PceRecord }) {
  const nowYear = new Date().getFullYear();
  const [preset,  setPreset]  = useState<SynthesePreset>("current");
  const [year,    setYear]    = useState(nowYear);
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth()/3) + 1);
  const [month,   setMonth]   = useState(new Date().getMonth() + 1);
  const [releves, setReleves] = useState<GazReleve[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string|null>(null);

  const months = useMemo(() => monthsForPreset(preset, year, quarter, month, nowYear), [preset, year, quarter, month, nowYear]);

  useEffect(() => {
    if (!meter.source) { setReleves([]); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const years = Array.from(new Set(months.map(m => parseInt(m.slice(0,4), 10))));
        const res = await getConsoGazDetail([{ id: meter.id, source: meter.source as GazSource }], years);
        if (cancelled) return;
        const all = res.perPce.find(p => p.id === meter.id)?.relevesGaz ?? [];
        const from = months[0], to = months[months.length - 1];
        // Un relevé dont la fin tombe exactement sur le 1er jour de la période affichée ne couvre
        // en réalité aucun jour de cette période (son dernier jour utile est la veille) — exclu
        // plutôt qu'affiché avec une durée quasi nulle.
        const periodStartDay = `${from}-01`;
        setReleves(all.filter(r => r.dateDebut.slice(0,7) <= to && r.dateFin.slice(0,7) >= from && r.dateFin !== periodStartDay));
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [meter.id, meter.source, months]);

  return (
    <div>
      <PeriodPresetSelector preset={preset} year={year} quarter={quarter} month={month}
        onChangePreset={setPreset} onChangeYear={setYear} onChangeQuarter={setQuarter} onChangeMonth={setMonth}
        hideDetailRow/>

      {!meter.source ? (
        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Source non configurée pour ce compteur.</div>
      ) : loading ? (
        <div style={{height:260,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:12}}>Chargement…</div>
      ) : error ? (
        <div style={{color:"var(--red)",fontSize:12}}>⚠ {error}</div>
      ) : !releves || releves.length === 0 ? (
        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Aucun relevé pour cette période.</div>
      ) : (
        <>
          <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:14}}>
            <PeriodDetailInputs preset={preset} year={year} quarter={quarter} month={month}
              onChangeYear={setYear} onChangeQuarter={setQuarter} onChangeMonth={setMonth}/>
            <div style={{marginLeft:"auto",display:"inline-flex",alignItems:"center",gap:8,padding:"8px 16px",borderRadius:8,
              background:"#fff5f0",border:"1px solid #f0d8c8"}}>
              <span style={{fontSize:11,color:"var(--text-muted)",fontFamily:"var(--ff-title)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>
                Total sur la période
              </span>
              <span style={{fontSize:16,fontWeight:700,color:"#e8703a",fontFamily:"var(--ff-title)"}}>
                {Math.round(releves.reduce((s,r) => s + r.energie, 0)).toLocaleString("fr-FR")} kWh
              </span>
            </div>
          </div>
          <GazReleveBarChart releves={releves}/>
        </>
      )}
    </div>
  );
}

type ElecGranularity = "heure" | "jour" | "mois";
const GRANULARITY_LABELS: Record<ElecGranularity, string> = { heure:"Heure", jour:"Jour", mois:"Mois" };

/** Bâtons de consommation ELEC — même scaffolding Highcharts que les autres graphiques de cette
 * page, mais colonnes de largeur égale (pas de module "variwide" nécessaire : les catégories sont
 * homogènes, contrairement aux relevés gaz de largeur irrégulière). */
function ElecConsoChart({ granularity, preset, categories, values }: {
  granularity: ElecGranularity; preset: SynthesePreset; categories: string[]; values: number[];
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const instRef  = useRef<any>(null);
  const doRender = useRef<() => void>(() => {});

  doRender.current = () => {
    if (!chartRef.current || !window.Highcharts) return;
    if (instRef.current) { try { instRef.current.destroy(); } catch {} instRef.current = null; }
    ((window.Highcharts.charts ?? []) as any[]).forEach(ch => {
      if (ch && ch.renderTo === chartRef.current) { try { ch.destroy(); } catch {} }
    });

    // "jour" peut couvrir jusqu'à une année entière (365 catégories, format "dd/mm") — un label par
    // jour serait illisible. Densité pilotée par la période (categories = axe non-datetime, pas
    // d'espacement auto par date, donc pas de tickInterval basé sur le temps) :
    //  - "current"/"year" (12 mois) : le 1er de chaque mois
    //  - "month" (~30 jours)        : un jour sur deux
    //  - "quarter" (~90 jours)      : pas de règle dédiée, repli sur un pas numérique régulier
    const dayLabels: any = granularity === "jour"
      ? (preset === "quarter"
          ? { step: Math.max(1, Math.ceil(categories.length / 12)) }
          : { formatter(this: any) {
              const day = parseInt(String(this.value).split("/")[0], 10);
              const show = preset === "month" ? day % 2 === 1 : day === 1;
              return show ? this.value : "";
            } })
      : {};

    instRef.current = window.Highcharts.chart(chartRef.current, {
      chart:{ type:"column", backgroundColor:"transparent", height:260, style:{ fontFamily: FF_ENERGY } },
      title:{ text: undefined }, legend:{ enabled:false }, credits:{ enabled:false },
      xAxis:{ categories, lineColor:"#ddd6c8", tickColor:"transparent",
        labels:{ style:{ fontSize:"10px", color:"var(--text-muted)" }, ...(granularity === "jour" ? { rotation:-45 } : {}), ...dayLabels } },
      yAxis:{ title:{ text:"kWh" }, gridLineColor:"#eee" },
      tooltip:{ pointFormat:"<b>{point.y:,.1f} kWh</b>" },
      series:[{ type:"column", name:"Consommation", data: values, color:"#f5c518" }],
    });
  };

  useEffect(() => {
    let active = true;
    loadHCAndRender(() => { if (active) doRender.current(); });
    return () => { active = false; if (instRef.current) { try { instRef.current.destroy(); } catch {} instRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (window.Highcharts) doRender.current(); }, [categories, values]);

  return <div ref={chartRef} style={{height:260,width:"100%"}}/>;
}

/** Consommations d'un compteur élec sur une période choisie, à la granularité choisie — mêmes
 * inputs de période que GazMeterReleves (PeriodPresetSelector) + un sélecteur heure/jour/mois.
 * "Heure" est un profil moyen (journée type, cf. getAvgDay) : ts-ingest n'expose aucune série
 * réelle datée plus fine que le jour (cf. /conso/journaliere/elec) — pas de relevé réel infra-
 * journalier disponible côté ELEC, contrairement au GAZ (relevesGaz). Monté avec `key={meter.id}`
 * par l'appelant. */
function ElecMeterConso({ meter }: { meter: PdlRecord }) {
  const nowYear = new Date().getFullYear();
  const [granularity, setGranularity] = useState<ElecGranularity>("jour");
  const [preset,  setPreset]  = useState<SynthesePreset>("current");
  const [year,    setYear]    = useState(nowYear);
  const [quarter, setQuarter] = useState(Math.floor(new Date().getMonth()/3) + 1);
  const [month,   setMonth]   = useState(new Date().getMonth() + 1);
  const [chart,   setChart]   = useState<{ categories: string[]; values: number[]; hasData: boolean; note?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string|null>(null);

  const periodKey = useMemo(() => periodKeyForPreset(preset, year, quarter, month, nowYear), [preset, year, quarter, month, nowYear]);
  const months    = useMemo(() => monthsForPreset(preset, year, quarter, month, nowYear), [preset, year, quarter, month, nowYear]);

  useEffect(() => {
    if (!meter.source) { setChart(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const taggedMeters: TaggedMeter[] = [{ id: meter.id, source: meter.source as MeterSource }];

        if (granularity === "jour") {
          const res = await getDailyElec(taggedMeters, periodKey);
          if (cancelled) return;
          const sorted = [...res.data].sort((a,b) => a[0] - b[0]);
          setChart({
            categories: sorted.map(([ts]) => new Date(ts).toLocaleDateString("fr-FR", { day:"2-digit", month:"2-digit" })),
            values: sorted.map(([,v]) => v),
            hasData: sorted.length > 0,
          });
        } else if (granularity === "mois") {
          const years = Array.from(new Set(months.map(m => parseInt(m.slice(0,4), 10))));
          const res = await getMonthlyElec(taggedMeters, years);
          if (cancelled) return;
          const monthValues = months.map(mk => res.consoElecTotaleParAnneeMois[mk]);
          setChart({
            categories: months.map(mk => monthLabel(`${mk}-01`)),
            values: monthValues.map(v => v ?? 0),
            hasData: monthValues.some(v => v != null),
          });
        } else {
          const res = await getAvgDay(taggedMeters, periodKey);
          if (cancelled) return;
          if (res.unsupported.some(u => u.id === meter.id)) {
            setChart({ categories: [], values: [], hasData: false, note: "Profil horaire indisponible pour ce compteur (pas de données horaires)." });
          } else {
            setChart({ categories: Array.from({length:24}, (_,h) => `${h}h`), values: res.data, hasData: res.data.length > 0 });
          }
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [meter.id, meter.source, granularity, periodKey, months]);

  const hasData = chart?.hasData ?? false;

  return (
    <div>
      <PeriodPresetSelector preset={preset} year={year} quarter={quarter} month={month}
        onChangePreset={setPreset} onChangeYear={setYear} onChangeQuarter={setQuarter} onChangeMonth={setMonth}
        hideDetailRow
        trailing={(Object.keys(GRANULARITY_LABELS) as ElecGranularity[]).map(g => (
          <button key={g} onClick={() => setGranularity(g)}
            style={{fontSize:11,padding:"5px 12px",borderRadius:20,
              border: granularity===g ? "1.5px solid var(--green-dim)" : "1.5px solid #ddd",
              background: granularity===g ? "var(--green-dim)" : "transparent",
              color: granularity===g ? "#fff" : "var(--text-muted)",
              cursor:"pointer", fontFamily:"var(--ff-title)", fontWeight:granularity===g?700:500}}>
            {GRANULARITY_LABELS[g]}
          </button>
        ))}/>

      {!meter.source ? (
        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Source non configurée pour ce compteur.</div>
      ) : loading ? (
        <div style={{height:260,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:12}}>Chargement…</div>
      ) : error ? (
        <div style={{color:"var(--red)",fontSize:12}}>⚠ {error}</div>
      ) : chart?.note ? (
        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>{chart.note}</div>
      ) : !hasData ? (
        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Aucune donnée pour cette période.</div>
      ) : (
        <>
          {granularity === "heure" && (
            <div style={{fontSize:11,color:"var(--text-faint)",fontStyle:"italic",marginBottom:10}}>
              Profil moyen (journée type) sur la période — pas un relevé réel daté.
            </div>
          )}
          <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:8,marginBottom:14}}>
            <PeriodDetailInputs preset={preset} year={year} quarter={quarter} month={month}
              onChangeYear={setYear} onChangeQuarter={setQuarter} onChangeMonth={setMonth}/>
            <div style={{marginLeft:"auto",display:"inline-flex",alignItems:"center",gap:8,padding:"8px 16px",borderRadius:8,
              background:"#fffbe6",border:"1px solid #ece0b0"}}>
              <span style={{fontSize:11,color:"var(--text-muted)",fontFamily:"var(--ff-title)",fontWeight:700,textTransform:"uppercase",letterSpacing:0.5}}>
                {granularity === "heure" ? "Total journée moyenne" : "Total sur la période"}
              </span>
              <span style={{fontSize:16,fontWeight:700,color:"#c8a000",fontFamily:"var(--ff-title)"}}>
                {Math.round(chart!.values.reduce((s,v) => s + v, 0)).toLocaleString("fr-FR")} kWh
              </span>
            </div>
          </div>
          <ElecConsoChart granularity={granularity} preset={preset} categories={chart!.categories} values={chart!.values}/>
        </>
      )}
    </div>
  );
}

interface Meter { record: PdlRecord | PceRecord; isPdl: boolean; }

export default function SuiviPage() {
  const { role } = useAuthContext();
  const perms = getPermissions(role);

  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [sites,   setSites]   = useState<SiteRecord[]>([]);
  const [pdls,    setPdls]    = useState<PdlRecord[]>([]);
  const [pces,    setPces]    = useState<PceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string|null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [query, setQuery] = useState("");
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [expandedSites,   setExpandedSites]   = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Selected|null>(null);
  const [siteDetailTab, setSiteDetailTab] = useState<SiteDetailTab>("synthese");
  const [openConso, setOpenConso] = useState<{ meter: PdlRecord|PceRecord; isPdl: boolean } | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  const [toast, setToast] = useState<string|null>(null);
  const showToast = (msg: string) => { setToast(msg); setTimeout(()=>setToast(null), 2500); };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [c, s, p, g] = await Promise.all([listClients(), listSites(), listPdl(), listPce()]);
      setClients(c); setSites(s); setPdls(p); setPces(g);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const [c, s, p, g] = await Promise.all([listClients(), listSites(), listPdl(), listPce()]);
      setClients(c); setSites(s); setPdls(p); setPces(g);
    } catch (e) { setError((e as Error).message); }
    finally { setRefreshing(false); }
  };

  const metersBySite = useMemo(() => {
    const map = new Map<string, Meter[]>();
    for (const p of pdls) {
      if (!map.has(p.siteId)) map.set(p.siteId, []);
      map.get(p.siteId)!.push({ record: p, isPdl: true });
    }
    for (const c of pces) {
      if (!map.has(c.siteId)) map.set(c.siteId, []);
      map.get(c.siteId)!.push({ record: c, isPdl: false });
    }
    for (const list of map.values()) list.sort((a,b) => a.record.id.localeCompare(b.record.id));
    return map;
  }, [pdls, pces]);

  const sitesByClient = useMemo(() => {
    const map = groupSitesByClient(sites);
    for (const list of map.values()) list.sort((a,b) => a.name.localeCompare(b.name));
    return map;
  }, [sites]);

  const q = query.trim().toLowerCase();
  const matchesMeter  = (m: Meter) => !q || m.record.id.toLowerCase().includes(q) || (m.record.label ?? "").toLowerCase().includes(q);
  const matchesSite   = (s: SiteRecord) => !q || s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q);
  const matchesClient = (c: ClientRecord) => !q || c.name.toLowerCase().includes(q);

  function siteVisible(site: SiteRecord, client: ClientRecord): boolean {
    if (!q) return true;
    if (matchesClient(client) || matchesSite(site)) return true;
    return (metersBySite.get(site.id) ?? []).some(matchesMeter);
  }
  function clientVisible(client: ClientRecord): boolean {
    if (!q) return true;
    if (matchesClient(client)) return true;
    return (sitesByClient.get(client.id) ?? []).some(s => siteVisible(s, client));
  }

  const selectClient = (id: string) => {
    setSelected({ kind:"client", id });
    setExpandedClients(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectSite = (site: SiteRecord) => {
    setSelected({ kind:"site", id: site.id });
    setSiteDetailTab("synthese");
    setExpandedClients(prev => new Set(prev).add(site.clientId));
    setExpandedSites(prev => { const n = new Set(prev); n.has(site.id) ? n.delete(site.id) : n.add(site.id); return n; });
  };
  const selectMeter = (meter: Meter) => {
    setSelected({ kind:"meter", id: meter.record.id, isPdl: meter.isPdl });
    const site = sites.find(s => s.id === meter.record.siteId);
    if (site) {
      setExpandedClients(prev => new Set(prev).add(site.clientId));
      setExpandedSites(prev => new Set(prev).add(site.id));
    }
  };

  const hasSelection = selected !== null;

  const selectedClient = selected?.kind === "client" ? clients.find(c => c.id === selected.id) : undefined;
  const selectedSite   = selected?.kind === "site"   ? sites.find(s => s.id === selected.id)   : undefined;
  const selectedMeter  = selected?.kind === "meter"
    ? (selected.isPdl ? pdls.find(p => p.id === selected.id) : pces.find(p => p.id === selected.id))
    : undefined;
  const selectedMeterIsPdl = selected?.kind === "meter" ? selected.isPdl : false;
  const selectedMeterSite  = selectedMeter ? sites.find(s => s.id === selectedMeter.siteId) : undefined;

  const siteDetailsFields = selectedSite ? PROP_LABELS.filter(([k]) => DETAILS_KEYS.includes(k) && selectedSite.data.coverProps[k]) : [];
  const siteCvcFields     = selectedSite ? PROP_LABELS.filter(([k]) => CVC_KEYS.includes(k)     && selectedSite.data.coverProps[k]) : [];

  return (
    <div className="screen" style={{height:"100%",overflow:"hidden",paddingBottom:0}}>
      <div className="screen-inner" style={{height:"100%",display:"flex",flexDirection:"column"}}>

        <div style={{flexShrink:0,display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
          <div>
            <h1 className="page-title">Suivi</h1>
            <p className="page-subtitle">Clients, sites et compteurs</p>
          </div>
          <button onClick={handleRefresh} disabled={refreshing} className="btn btn-ghost"
            title="Rafraîchir" style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"9px 14px"}}>
            <span style={{fontSize:18,lineHeight:1}}>↻</span>
          </button>
        </div>

        {loading ? (
          <div style={{textAlign:"center",padding:48,color:"var(--text-faint)"}}>Chargement…</div>
        ) : error ? (
          <div style={{color:"var(--red)",fontSize:13}}>⚠ {error}</div>
        ) : (
          <div style={{flex:1,minHeight:0,display:"flex",gap:20}}>

            {/* ── Arborescence ─────────────────────────────────────────────────────────── */}
            <div className="card" style={{
              width: hasSelection ? 300 : "100%",
              flexShrink: 0,
              transition:"width 0.15s ease",
              display:"flex", flexDirection:"column", minHeight:0,
            }}>
              <div style={{flexShrink:0,padding:"12px 14px",borderBottom:"1px solid var(--border-light)"}}>
                <input
                  type="text"
                  placeholder="Rechercher un client, site, compteur…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  className="field"
                  style={{width:"100%",fontSize:13,marginBottom:0}}
                />
              </div>

              <div style={{flex:1,minHeight:0,overflowY:"auto",padding:"6px 0"}}>
                {clients.length === 0 ? (
                  <div style={{padding:24,textAlign:"center",color:"var(--text-faint)",fontSize:13}}>Aucun client.</div>
                ) : clients.filter(clientVisible).length === 0 ? (
                  <div style={{padding:24,textAlign:"center",color:"var(--text-faint)",fontSize:13}}>Aucun résultat pour « {query} ».</div>
                ) : (
                  clients.filter(clientVisible).map(client => {
                    const clientSites = (sitesByClient.get(client.id) ?? []).filter(s => siteVisible(s, client));
                    const clientOpen = q ? true : expandedClients.has(client.id);
                    const isSelClient = selected?.kind === "client" && selected.id === client.id;
                    return (
                      <div key={client.id}>
                        <button onClick={() => selectClient(client.id)}
                          style={{display:"flex",alignItems:"center",gap:6,width:"100%",textAlign:"left",
                            background:isSelClient?"#eefbea":"none",border:"none",cursor:"pointer",
                            padding:"7px 14px",fontFamily:"var(--ff-title)"}}>
                          <Chevron open={clientOpen}/>
                          <span style={{fontSize:15,flexShrink:0}}>🏢</span>
                          <span style={{fontSize:13,fontWeight:700,color:"var(--text-primary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>
                            {client.name}
                          </span>
                          <span style={{fontSize:11,color:"var(--text-faint)",flexShrink:0}}>{clientSites.length}</span>
                        </button>

                        {clientOpen && clientSites.map(site => {
                          const siteMeters = (metersBySite.get(site.id) ?? []).filter(matchesMeter);
                          const siteOpen = q ? true : expandedSites.has(site.id);
                          const isSelSite = selected?.kind === "site" && selected.id === site.id;
                          return (
                            <div key={site.id}>
                              <button onClick={() => selectSite(site)}
                                style={{display:"flex",alignItems:"center",gap:6,width:"100%",textAlign:"left",
                                  background:isSelSite?"#eefbea":"none",border:"none",cursor:"pointer",
                                  padding:"6px 14px 6px 30px",fontFamily:"var(--ff-title)"}}>
                                <Chevron open={siteOpen}/>
                                <span style={{fontSize:14,flexShrink:0}}>🏗</span>
                                <span style={{fontSize:12.5,fontWeight:600,color:"var(--text-primary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>
                                  {site.name}
                                </span>
                                <span style={{fontSize:11,color:"var(--text-faint)",flexShrink:0}}>{siteMeters.length}</span>
                              </button>

                              {siteOpen && siteMeters.map(meter => {
                                const isSelMeter = selected?.kind === "meter" && selected.id === meter.record.id && selected.isPdl === meter.isPdl;
                                return (
                                  <button key={`${meter.isPdl?"pdl":"pce"}-${meter.record.id}`} onClick={() => selectMeter(meter)}
                                    style={{display:"flex",alignItems:"center",gap:6,width:"100%",textAlign:"left",
                                      background:isSelMeter?"#eefbea":"none",border:"none",cursor:"pointer",
                                      padding:"5px 14px 5px 48px",fontFamily:"var(--ff-title)"}}>
                                    <span style={{fontSize:12,flexShrink:0}}>{meter.isPdl ? "⚡" : "🔥"}</span>
                                    <span style={{fontSize:12,color:"var(--text-secondary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>
                                      {meter.record.id}
                                    </span>
                                    {meter.isPdl && (meter.record as PdlRecord).source === "enedis" && !(meter.record as PdlRecord).sge && (
                                      <span title="Non accessible via SGE" style={{fontSize:12,flexShrink:0}}>🚫</span>
                                    )}
                                  </button>
                                );
                              })}
                              {siteOpen && siteMeters.length === 0 && (
                                <div style={{padding:"4px 14px 6px 48px",fontSize:11,color:"var(--text-faint)",fontStyle:"italic"}}>
                                  Aucun compteur
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* ── Détail du nœud sélectionné ───────────────────────────────────────────── */}
            {hasSelection && (
              <div className="card" style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",minHeight:0}}>
                <div style={{flexShrink:0,padding:"14px 20px",borderBottom:"1px solid var(--border-light)",
                  display:"flex",alignItems:"center",gap:10}}>

                  {selected?.kind === "client" && selectedClient && (
                    <div style={{display:"flex",alignItems:"center",gap:12,flex:1,minWidth:0}}>
                      {selectedClient.logoBase64 && (
                        <img src={selectedClient.logoBase64} alt="" style={{width:36,height:36,objectFit:"contain",borderRadius:6,border:"1px solid var(--border-light)",flexShrink:0}}/>
                      )}
                      <div style={{minWidth:0}}>
                        <div className="t-title" style={{fontSize:15,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedClient.name}</div>
                        <div style={{fontSize:11,color:"var(--text-faint)"}}>{selectedClient.id}</div>
                      </div>
                    </div>
                  )}

                  {selected?.kind === "site" && selectedSite && (
                    <button onClick={() => selectClient(selectedSite.clientId)}
                      style={{display:"flex",alignItems:"center",gap:8,background:"none",border:"none",cursor:"pointer",
                        padding:0,flex:1,minWidth:0,textAlign:"left"}}>
                      <span style={{fontSize:16,color:"var(--text-faint)",flexShrink:0}}>←</span>
                      <span className="t-title" style={{fontSize:15,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedSite.name}</span>
                    </button>
                  )}

                  {selected?.kind === "meter" && selectedMeter && (
                    <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0}}>
                      <button onClick={() => selectedMeterSite && selectSite(selectedMeterSite)}
                        style={{background:"none",border:"none",cursor:"pointer",padding:0,fontSize:16,color:"var(--text-faint)",flexShrink:0}}>
                        ←
                      </button>
                      <span style={{fontSize:18,flexShrink:0}}>{selectedMeterIsPdl ? "⚡" : "🔥"}</span>
                      <span className="t-title" style={{fontSize:15,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selectedMeter.id}</span>
                      <SourceBadge source={selectedMeter.source}/>
                      {selectedMeterIsPdl && (selectedMeter as PdlRecord).source === "enedis" && !(selectedMeter as PdlRecord).sge && (
                        <span title="Non accessible via SGE" style={{fontSize:14,flexShrink:0}}>🚫</span>
                      )}
                      <button
                        onClick={() => setOpenConso({ meter: selectedMeter, isPdl: selectedMeterIsPdl })}
                        title="Voir les consommations"
                        style={{background:"none",border:"1px solid #ddd",borderRadius:6,cursor:"pointer",padding:"4px 7px",
                          color:"var(--text-muted)",fontSize:13,lineHeight:1,flexShrink:0}}>
                        ▦
                      </button>
                    </div>
                  )}

                  <button onClick={() => setSelected(null)} title="Fermer"
                    style={{background:"none",border:"none",cursor:"pointer",fontSize:18,lineHeight:1,color:"var(--text-faint)",flexShrink:0}}>
                    ✕
                  </button>
                </div>

                <div style={{flex:1,minHeight:0,overflowY:"auto",padding:"20px 24px"}}>
                  {/* Client */}
                  {selected?.kind === "client" && (selectedClient ? (
                    <>
                      <div className="section-title" style={{marginBottom:10}}>
                        Sites ({(sitesByClient.get(selectedClient.id) ?? []).length})
                      </div>
                      {(sitesByClient.get(selectedClient.id) ?? []).length === 0 ? (
                        <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Aucun site pour ce client.</div>
                      ) : (
                        <div className="flex-col gap-8">
                          {(sitesByClient.get(selectedClient.id) ?? []).map(site => (
                            <button key={site.id} onClick={() => selectSite(site)}
                              className="card" style={{textAlign:"left",padding:"12px 16px",cursor:"pointer",border:"1px solid var(--border-light)",background:"var(--bg-card)",width:"100%"}}>
                              <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)",fontFamily:"var(--ff-title)"}}>{site.name}</div>
                              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>
                                {site.data.coverProps?.adresse || "Adresse non renseignée"}
                                {" · "}{(metersBySite.get(site.id) ?? []).length} compteur{(metersBySite.get(site.id) ?? []).length > 1 ? "s" : ""}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : <div style={{fontSize:13,color:"var(--text-faint)"}}>Client introuvable.</div>)}

                  {/* Site */}
                  {selected?.kind === "site" && (selectedSite ? (
                    <>
                      {/* Sous-onglets Info / Synthèse */}
                      <div style={{display:"flex",gap:4,marginBottom:18,background:"#f2f2f2",borderRadius:8,padding:4,width:"fit-content"}}>
                        {([["info","Info"],["synthese","Synthèse"]] as [SiteDetailTab,string][]).map(([id,label]) => (
                          <button key={id} onClick={() => setSiteDetailTab(id)}
                            style={{border:"none",cursor:"pointer",padding:"7px 18px",borderRadius:6,
                              fontFamily:"var(--ff-title)",fontSize:12,fontWeight:700,
                              background:siteDetailTab===id?"#fff":"transparent",
                              color:siteDetailTab===id?"var(--text-primary)":"var(--text-muted)",
                              boxShadow:siteDetailTab===id?"0 1px 4px rgba(0,0,0,0.10)":"none",
                              transition:"all 0.15s"}}>
                            {label}
                          </button>
                        ))}
                      </div>

                      {siteDetailTab === "info" && (
                        <>
                          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
                            {selectedSite.data.coverProps?.photoUrl ? (
                              <ImageThumb label="Photo du site" src={selectedSite.data.coverProps.photoUrl}
                                onOpen={() => setLightbox({ src: selectedSite.data.coverProps.photoUrl, alt: "Photo du site" })}/>
                            ) : <EmptyThumb label="Photo du site" icon="📷"/>}
                            {selectedSite.data.schemaCVC ? (
                              <ImageThumb label="Schéma CVC" src={selectedSite.data.schemaCVC}
                                onOpen={() => setLightbox({ src: selectedSite.data.schemaCVC, alt: "Schéma CVC" })}/>
                            ) : <EmptyThumb label="Schéma CVC" icon="🔧"/>}
                          </div>

                          {selectedSite.data.description && (
                            <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:14}}>{selectedSite.data.description}</div>
                          )}
                          <div style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:12,color:"var(--text-muted)",marginBottom:18}}>
                            <span>📍 {selectedSite.data.coverProps?.adresse || "Adresse non renseignée"}</span>
                            {selectedSite.data.coverProps?.lat && selectedSite.data.coverProps?.lon && (
                              <span>🌐 {selectedSite.data.coverProps.lat}, {selectedSite.data.coverProps.lon}</span>
                            )}
                          </div>

                          {siteDetailsFields.length > 0 && (
                            <>
                              <div className="section-title" style={{marginBottom:8}}>Détails</div>
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12,color:"var(--text-muted)",marginBottom:18}}>
                                {siteDetailsFields.map(([k,lbl]) => (
                                  <span key={k}>{lbl} : <strong style={{color:"var(--text-primary)"}}>{selectedSite.data.coverProps[k]}</strong></span>
                                ))}
                              </div>
                            </>
                          )}

                          {(siteCvcFields.length > 0 || selectedSite.data.coverProps?.gtb) && (
                            <>
                              <div className="section-title" style={{marginBottom:8}}>CVC</div>
                              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12,color:"var(--text-muted)",marginBottom:18}}>
                                {siteCvcFields.map(([k,lbl]) => (
                                  <span key={k}>{lbl} : <strong style={{color:"var(--text-primary)"}}>{selectedSite.data.coverProps[k]}</strong></span>
                                ))}
                                {selectedSite.data.coverProps?.gtb && (
                                  <span>GTB : <strong style={{color:"var(--text-primary)"}}>{selectedSite.data.coverProps.gtb}</strong></span>
                                )}
                              </div>
                            </>
                          )}

                          <div className="section-title" style={{marginBottom:10}}>
                            Compteurs ({(metersBySite.get(selectedSite.id) ?? []).length})
                          </div>
                          {(metersBySite.get(selectedSite.id) ?? []).length === 0 ? (
                            <div style={{fontSize:13,color:"var(--text-faint)",fontStyle:"italic"}}>Aucun compteur rattaché à ce site.</div>
                          ) : (
                            <div className="flex-col gap-8">
                              {(metersBySite.get(selectedSite.id) ?? []).map(meter => (
                                <button key={`${meter.isPdl?"pdl":"pce"}-${meter.record.id}`} onClick={() => selectMeter(meter)}
                                  className="card" style={{textAlign:"left",padding:"10px 16px",cursor:"pointer",
                                    border:"1px solid var(--border-light)",background:"var(--bg-card)",width:"100%",
                                    display:"flex",alignItems:"center",gap:10}}>
                                  <span style={{fontSize:16}}>{meter.isPdl ? "⚡" : "🔥"}</span>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:13,fontWeight:700,color:"var(--text-primary)",fontFamily:"var(--ff-title)",display:"flex",alignItems:"center",gap:6}}>
                                      {meter.record.id}
                                      {meter.isPdl && (meter.record as PdlRecord).source === "enedis" && !(meter.record as PdlRecord).sge && (
                                        <span title="Non accessible via SGE">🚫</span>
                                      )}
                                    </div>
                                    {meter.record.label && <div style={{fontSize:11,color:"var(--text-muted)"}}>{meter.record.label}</div>}
                                  </div>
                                  <SourceBadge source={meter.record.source}/>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}

                      {siteDetailTab === "synthese" && (
                        <SiteSyntheseTab key={selectedSite.id} site={selectedSite} pdls={pdls} pces={pces}/>
                      )}
                    </>
                  ) : <div style={{fontSize:13,color:"var(--text-faint)"}}>Site introuvable.</div>)}

                  {/* Compteur */}
                  {selected?.kind === "meter" && (selectedMeter ? (
                    <>
                      <div style={{display:"flex",gap:20,flexWrap:"wrap",fontSize:12,color:"var(--text-muted)",marginBottom:20}}>
                        {selectedMeter.label && <span>Libellé : <strong>{selectedMeter.label}</strong></span>}
                        {selectedMeterIsPdl && (selectedMeter as PdlRecord).segment && <span>Segment : <strong>{(selectedMeter as PdlRecord).segment}</strong></span>}
                        {selectedMeterIsPdl && (selectedMeter as PdlRecord).puissanceSouscrite && <span>P. souscrite : <strong>{(selectedMeter as PdlRecord).puissanceSouscrite}</strong></span>}
                        {selectedMeter.codePostal && <span>CP : <strong>{selectedMeter.codePostal}</strong></span>}
                        <span>Site : <strong>{selectedMeterSite?.name ?? "non rattaché"}</strong></span>
                      </div>

                      {selectedMeterIsPdl ? (
                        <>
                          <div className="section-title" style={{margin:"24px 0 10px"}}>Consommations</div>
                          <ElecMeterConso key={selectedMeter.id} meter={selectedMeter as PdlRecord}/>
                        </>
                      ) : (
                        <>
                          <div className="section-title" style={{margin:"24px 0 10px"}}>Relevés</div>
                          <GazMeterReleves key={selectedMeter.id} meter={selectedMeter as PceRecord}/>
                        </>
                      )}
                    </>
                  ) : <div style={{fontSize:13,color:"var(--text-faint)"}}>Compteur introuvable.</div>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {openConso && (
        <MeterConsoModal
          meter={openConso.meter} isPdl={openConso.isPdl}
          site={sites.find(s => s.id === openConso.meter.siteId)}
          showToast={showToast}
          canPurgeDeepki={perms.canManageRoles}
          onClose={() => setOpenConso(null)}
        />
      )}

      {lightbox && (
        <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)}/>
      )}

      {toast && (
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",
          background:"#1a1a2e",color:"#fff",padding:"10px 20px",borderRadius:8,
          fontSize:13,boxShadow:"0 4px 16px rgba(0,0,0,0.3)",zIndex:9999}}>
          {toast}
        </div>
      )}
    </div>
  );
}
